import {
    commonProperties,
    defaultFieldForValueType,
    fieldConstraints,
    fieldInputs,
    fieldProperties,
    inputAllowedValueTypes,
    valueTypes,
    type FieldInput,
    type ValueType,
} from '../../../../shared/contracts/form-schema/field-type-registry';

export interface PssFormField {
    id: string;
    label: string;
    key: string;
    type: ValueType;
    input?: FieldInput;
    defaultValue?: string;
    defaultValueMode?: 'literal' | 'expression';
    constraints: Record<string, string>;
    properties: Record<string, string>;
    passthroughExtensions?: Record<string, string>;
}

export interface WorkflowFormField {
    id: string;
    key: string;
    label: string;
    type: string;
    valueType: string;
    defaultValue?: string;
    required: boolean;
    disabled: boolean;
    hidden: boolean;
    modifier?: string;
    group?: string;
    extensions?: Record<string, string>;
}

function resolveInput(field: PssFormField): FieldInput {
    if (field.input && inputAllowedValueTypes[field.input]?.includes(field.type)) return field.input;
    return defaultFieldForValueType[field.type];
}

function toWireValueType(type: ValueType): string {
    if (type === 'number') return 'long';
    if (type === 'bool') return 'boolean';
    if (type === 'present') return 'string';
    return type;
}

function toUiValueType(valueType: unknown, input: FieldInput): ValueType {
    if (['pdfViewer', 'badgePhoto', 'badgePreviewFront', 'badgePreviewBack'].includes(input)) return 'present';
    if (valueType === 'long' || valueType === 'number') return 'number';
    if (valueType === 'boolean' || valueType === 'bool') return 'bool';
    return valueTypes.includes(valueType as ValueType) ? valueType as ValueType : 'string';
}

function toWorkflowEditorType(input: FieldInput): string {
    if (input === 'switch') return 'boolean';
    if (input === 'dateRangeBegin' || input === 'dateRangeEnd') return 'dateRange';
    return input;
}

function toUiInput(type: unknown, extensions: Record<string, string>, valueType: unknown): FieldInput {
    if (type === 'checkbox' || type === 'boolean') return 'switch';
    if (type === 'datepicker') return valueType === 'datetime' ? 'datetime' : 'date';
    if (type === 'dateRange') return extensions.dateRangeSide === 'end' ? 'dateRangeEnd' : 'dateRangeBegin';
    if (fieldInputs.includes(type as FieldInput)) return type as FieldInput;
    return defaultFieldForValueType[toUiValueType(valueType, 'text')];
}

export function serializePssFormField(field: PssFormField): WorkflowFormField {
    const input = resolveInput(field);
    const extensions: Record<string, string> = {
        ...(field.passthroughExtensions || {}),
        ...field.constraints,
        ...field.properties,
    };

    delete extensions.required;
    delete extensions.hidden;
    delete extensions.disabled;
    delete extensions.modifier;
    delete extensions.group;

    if (input === 'dateRangeBegin' || input === 'dateRangeEnd') {
        extensions.dateRangeSide = input === 'dateRangeEnd' ? 'end' : 'begin';
    }

    return {
        id: field.id,
        key: field.key,
        label: field.label,
        type: toWorkflowEditorType(input),
        valueType: toWireValueType(field.type),
        defaultValue: field.defaultValue || undefined,
        required: field.constraints.required === 'true',
        disabled: field.properties.disabled === 'true',
        hidden: field.properties.hidden === 'true',
        modifier: field.properties.modifier || undefined,
        group: field.properties.group || undefined,
        extensions: Object.keys(extensions).length > 0 ? extensions : undefined,
    };
}

export function deserializePssFormField(value: unknown, index: number): PssFormField {
    const field = value && typeof value === 'object' ? value as Record<string, unknown> : {};
    if (
        valueTypes.includes(field.type as ValueType)
        && field.constraints && typeof field.constraints === 'object'
        && field.properties && typeof field.properties === 'object'
    ) {
        const type = field.type as ValueType;
        const candidateInput = field.input as FieldInput | undefined;
        const input = candidateInput && inputAllowedValueTypes[candidateInput]?.includes(type)
            ? candidateInput
            : defaultFieldForValueType[type];
        const id = typeof field.id === 'string' ? field.id : `field_${index + 1}`;
        const defaultValue = typeof field.defaultValue === 'string' ? field.defaultValue : undefined;
        return {
            id,
            key: typeof field.key === 'string' ? field.key : id,
            label: typeof field.label === 'string' ? field.label : `Field #${index + 1}`,
            type,
            input,
            defaultValue,
            defaultValueMode: field.defaultValueMode === 'expression' || (defaultValue && /^\$\{[\s\S]*\}$/.test(defaultValue.trim()))
                ? 'expression'
                : 'literal',
            constraints: { ...(field.constraints as Record<string, string>) },
            properties: { ...(field.properties as Record<string, string>) },
        };
    }

    const extensions = field.extensions && typeof field.extensions === 'object'
        ? { ...(field.extensions as Record<string, string>) }
        : {};
    const input = toUiInput(field.type, extensions, field.valueType);
    const type = toUiValueType(field.valueType, input);
    const constraintNames = new Set(Object.keys(fieldConstraints[input] || {}));
    const propertyNames = new Set([
        ...Object.keys(commonProperties),
        ...Object.keys(fieldProperties[input] || {}),
    ]);
    const constraints: Record<string, string> = {};
    const properties: Record<string, string> = {};
    const passthroughExtensions: Record<string, string> = {};

    if (field.required === true) constraints.required = 'true';
    if (field.disabled === true) properties.disabled = 'true';
    if (field.hidden === true) properties.hidden = 'true';
    if (typeof field.modifier === 'string' && field.modifier) properties.modifier = field.modifier;
    if (typeof field.group === 'string' && field.group) properties.group = field.group;

    for (const [name, extensionValue] of Object.entries(extensions)) {
        if (name === 'dateRangeSide') continue;
        if (constraintNames.has(name)) constraints[name] = extensionValue;
        else if (propertyNames.has(name)) properties[name] = extensionValue;
        else passthroughExtensions[name] = extensionValue;
    }

    const id = typeof field.id === 'string' ? field.id : `field_${index + 1}`;
    const defaultValue = typeof field.defaultValue === 'string' ? field.defaultValue : undefined;
    return {
        id,
        key: typeof field.key === 'string' ? field.key : id,
        label: typeof field.label === 'string' ? field.label : `Field #${index + 1}`,
        type,
        input,
        defaultValue,
        defaultValueMode: defaultValue && /^\$\{[\s\S]*\}$/.test(defaultValue.trim()) ? 'expression' : 'literal',
        constraints,
        properties,
        passthroughExtensions: Object.keys(passthroughExtensions).length > 0 ? passthroughExtensions : undefined,
    };
}
